import type { FC } from 'react';
import React, { useContext, useMemo } from 'react';
import type { AxiosError } from 'axios';

import noop from '../../../utils/function/noop';
import {
  getCustomerId,
  getCardCategories,
} from '../../../utils/session/selectors';
import SycurioSubmitPaymentView, {
  type SycurioPaymentMethodFormSubmitData,
} from '../sycurio-submit-payment-view/SycurioSubmitPaymentView';
import { NotificationContext } from '../../contextStore/NotificationContext';
import useSessionExpirationReminder from '../../../hooks/useSessionExpirationReminder';
import processSycurioPaymentSubmission from '../../../utils/capabilities/processSycurioPaymentSubmission';
import type ComponentEntryLocalData from '../../../shared/types/component-entry/ComponentEntryLocalData';
import { AppContext } from '../../contextStore/AppContext';
import type PaymentApi from '../../../services/payment/PaymentApi';
import {
  generateSycurioData,
  isTelephonicEntryLocalDataValid,
} from '../utilities/generateSycurioSubmitObject';
import ErrorCard from '../../../components/ErrorCard/ErrorCard';
import type { ApiError } from '../../checkout-v2/types';
import { getMedicationOptionsFromManufacturerCardTypeConfig } from '../../../utils/session/getMedicationOptionsFromManufacturerCardTypeConfig';
import { MedicationOption } from '../../../shared/types/CardCategories/MedicationOption';

/**
 * Props for SycurioSubmitPaymentContainer.
 * @param medicationOptions - List of medication options for manufacturer cards.
 * @param cardCategoriesConfigObject - Card categories configuration object.
 */
export type SycurioSubmitPaymentContainerProps = {
  localData: ComponentEntryLocalData;
  merchantTransactionId: string;
  amount: number;
  authorizeCard?: boolean;
  paymentDescription: string;
  statementDescriptorSuffix?: string;
  paymentApi: typeof PaymentApi;
  showSaveFeatureBlock: boolean;
  onSuccess?: () => void;
  onCancel: () => void;
  onBeforeSubmit?: () => void;
  onBackClick?: () => void;
};

const SycurioSubmitPaymentContainer: FC<
  SycurioSubmitPaymentContainerProps
> = ({
  localData,
  merchantTransactionId,
  amount,
  paymentDescription,
  authorizeCard,
  statementDescriptorSuffix,
  showSaveFeatureBlock,
  paymentApi,
  onSuccess = noop,
  onCancel = noop,
  onBeforeSubmit = noop,
  onBackClick,
}) => {
  const {
    setData,
    originalCheckoutSessionResponse,
    initialSessionData,
  } = useContext(AppContext);

  const { notify } = useContext(NotificationContext);
  const medicationOptions: MedicationOption[] = useMemo(
    () =>
      getMedicationOptionsFromManufacturerCardTypeConfig(
        initialSessionData,
      ),
    [initialSessionData],
  );

  const cardCategoriesConfig = useMemo(
    () => getCardCategories(initialSessionData),
    [initialSessionData],
  );

  useSessionExpirationReminder({
    notify,

    originalCheckoutSessionResponse,
  });

  const customerId = getCustomerId(
    originalCheckoutSessionResponse,
  );

  const onProcessingComplete = () => {
    setData({ overlayLoaderConfig: { show: false } });
  };

  const handleError = (err: AxiosError<ApiError>) => {
    const detailMessage =
      err?.response?.data?.detail || 'An error occurred';

    if (onBackClick) {
      onBackClick();
    }
    notify({
      severity: 'error',
      title: detailMessage,
    });
  };

  const onSubmit = ({
    nameOnCard,
    nickname,
    manufacturerCard,
    savePaymentMethod,
    isDefault,
    paymentMethodId,
    last4,
    medication,
  }: SycurioPaymentMethodFormSubmitData) => {
    setData({ overlayLoaderConfig: { show: true } });
    onBeforeSubmit();

    processSycurioPaymentSubmission({
      nameOnCard,
      nickname,
      manufacturerCard,
      savePaymentMethod,
      isDefault,
      paymentMethodId,
      last4,
      originalCheckoutSessionResponse,
      amount,
      merchantTransactionId,
      authorizeCard,
      statementDescriptorSuffix,
      paymentDescription,
      paymentApi,
      medicationOptions,
      medication,
      onSuccess,
      onProcessingComplete,
    }).catch((err: AxiosError<ApiError>) => {
      handleError(err);
    });
  };

  const isValidData =
    isTelephonicEntryLocalDataValid(localData) &&
    localData?.paymentMethodId;

  if (!isValidData) {
    return <ErrorCard />;
  }

  return (
    <SycurioSubmitPaymentView
      sycurioData={generateSycurioData(localData)}
      paymentMethodId={localData.paymentMethodId as string}
      isGuestUser={!customerId}
      amount={amount}
      showSaveFeatureBlock={showSaveFeatureBlock}
      formTitle="Make a payment via phone entry"
      onBackClick={onBackClick}
      onSubmit={onSubmit}
      onCancel={onCancel}
      medicationOptions={medicationOptions}
      cardCategoriesConfigObject={cardCategoriesConfig}
    />
  );
};

export default SycurioSubmitPaymentContainer;
